var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableCell, TableRow } from '../../common';
import { useCurrencySymbol } from '../../hooks/useCurrencyOptions';
import { QueueType } from '../../multiskill/types';
import { useResourcingContext } from '../../report/hooks';
import { DisplayMode } from '../../report/resourcing';
import { useDisplayModeParams } from '../../report/selectors';
import { PLAN_COLORS } from '../../wizard/constants';
import { FirstCell, STICKIES } from '../annualTable';
import { AnnualSectionTable } from '../common/annualSectionTable';
import { ExpandableTotalSection } from '../common/expandableTotalSection';
import { buildMultiskillGroupExpansionKeys, getCountryExpansionKey, getMultiskillCategoryExpansionKey } from '../common/selectors';
import { getChunkBaselineValue } from '../common/utils';
import { MULTISKILL_COLOR, TOTAL_ROW_COLOR } from '../constants';
import { generateVariances, useSelectCurrency, useSummaryByDisplayMode, useSummaryChunkSize } from '../selectors';
import { useSelectSupportStaffsPerScenario } from './selectors';
import { SupportStaffRoleRow, getSupportStaffLobExpansionKey } from './supportStaffRoleRow';
var getSupportStaffQueueExpansionKey = function (lobExpansionKey, isNonDeferrable, queueName) {
  return "".concat(lobExpansionKey, ":queue:").concat(isNonDeferrable ? 'nonDeferrable' : 'deferrable', ":").concat(queueName);
};
var getSupportStaffExpansionKeys = function (supportStaffs) {
  return supportStaffs.flatMap(function (roleEntry) {
    return roleEntry.queuesByLob.flatMap(function (lob) {
      var lobExpansionKey = getSupportStaffLobExpansionKey(roleEntry.roleName, lob.lobName);
      var queueEntries = __spreadArray(__spreadArray([], lob.defQueues.map(function (queue) {
        return {
          queue: queue,
          isNonDeferrable: false
        };
      }), true), lob.nonDefQueues.map(function (queue) {
        return {
          queue: queue,
          isNonDeferrable: true
        };
      }), true);
      var queueExpansionKeys = queueEntries.map(function (_a) {
        var queue = _a.queue,
          isNonDeferrable = _a.isNonDeferrable;
        return getSupportStaffQueueExpansionKey(lobExpansionKey, isNonDeferrable, queue.name);
      });
      var countryExpansionKeys = queueEntries.flatMap(function (_a) {
        var queue = _a.queue,
          isNonDeferrable = _a.isNonDeferrable;
        var queueExpansionKey = getSupportStaffQueueExpansionKey(lobExpansionKey, isNonDeferrable, queue.name);
        return queue.countries.map(function (country) {
          return getCountryExpansionKey(queueExpansionKey, country.countryName);
        });
      });
      return __spreadArray(__spreadArray([lobExpansionKey], queueExpansionKeys, true), countryExpansionKeys, true);
    });
  });
};
var getRoleNestedExpansionItems = function (roleName) {
  return [getMultiskillCategoryExpansionKey(roleName)];
};
var useSupportStaffExpansion = function (rootExpansionKey) {
  var data = useSummaryByDisplayMode();
  var supportStaffs = useSelectSupportStaffsPerScenario();
  var roles = useMemo(function () {
    return supportStaffs.map(function (s) {
      return s.roleName;
    });
  }, [supportStaffs]);
  var nestedExpansionItems = useMemo(function () {
    return getSupportStaffExpansionKeys(supportStaffs);
  }, [supportStaffs]);
  var multiskillExpansionItems = useMemo(function () {
    return roles.flatMap(function (roleName) {
      return buildMultiskillGroupExpansionKeys(data !== null && data !== void 0 ? data : [], roleName);
    });
  }, [data, roles]);
  var expansionItems = __spreadArray(__spreadArray(__spreadArray([rootExpansionKey], roles, true), nestedExpansionItems, true), multiskillExpansionItems, true);
  var getNestedExpansionItems = function (itemName) {
    if (itemName === rootExpansionKey) return __spreadArray(__spreadArray(__spreadArray([], roles, true), nestedExpansionItems, true), multiskillExpansionItems, true);
    if (roles.includes(itemName)) return getRoleNestedExpansionItems(itemName);
    return [];
  };
  return {
    expansionItems: expansionItems,
    getNestedExpansionItems: getNestedExpansionItems
  };
};
export var SupportStaffGenericSection = function (_a) {
  var _b = _a.valueKey,
    valueKey = _b === void 0 ? 'value' : _b,
    _c = _a.decimal,
    decimal = _c === void 0 ? 1 : _c,
    currencySymbol = _a.currencySymbol,
    totalFieldKey = _a.totalFieldKey,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem,
    totalAsParent = _a.totalAsParent;
  var t = useTranslation('report').t;
  var data = useSummaryByDisplayMode();
  var chunkSize = useSummaryChunkSize();
  var supportStaffsPerScenario = useSelectSupportStaffsPerScenario();
  var isPercentages = useResourcingContext().isPercentages;
  var displayMode = useDisplayModeParams()[0];
  var _d = useCurrencySymbol(currencySymbol),
    currencyPrefix = _d.prefix,
    currencySuffix = _d.suffix;
  var varianceOptions = {
    decimalPlaces: currencySymbol ? 0 : undefined,
    currencyPrefix: currencyPrefix,
    currencySuffix: currencySuffix
  };
  if (!data) return null;
  var totalPerScenario = data.map(function (s) {
    var _a;
    return (_a = s[totalFieldKey]) !== null && _a !== void 0 ? _a : null;
  });
  var roleRows = supportStaffsPerScenario.map(function (roleEntry) {
    var scenarioValues = (valueKey === 'supportStaffSalaries' ? roleEntry.salaryValues : roleEntry.values).flat().map(function (v) {
      return v !== null && v !== void 0 ? v : null;
    });
    return _jsx(SupportStaffRoleRow, {
      roleName: roleEntry.roleName,
      scenarioValues: scenarioValues,
      queuesByLob: roleEntry.queuesByLob,
      perScenarioQueues: roleEntry.perScenarioQueues,
      data: data,
      chunkSize: chunkSize,
      expandedProp: expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[roleEntry.roleName],
      onToggle: function () {
        return onToggleItem === null || onToggleItem === void 0 ? void 0 : onToggleItem(roleEntry.roleName);
      },
      expandedItems: expandedItems,
      onToggleItem: onToggleItem,
      valueKey: valueKey,
      decimal: decimal,
      currencySymbol: currencySymbol
    }, roleEntry.roleName);
  });
  if (totalAsParent) {
    return _jsx(ExpandableTotalSection, __assign({
      label: t("annual.".concat(totalFieldKey)),
      valueKey: totalFieldKey,
      expansionKey: totalFieldKey,
      expandedItems: expandedItems,
      onToggleItem: onToggleItem,
      decimal: decimal,
      hasChildren: roleRows.length > 0
    }, {
      children: roleRows
    }));
  }
  return _jsxs(_Fragment, {
    children: [roleRows, _jsxs(TableRow, {
      children: [_jsx(FirstCell, {
        stickySizes: STICKIES,
        bold: true,
        value: t("annual.".concat(totalFieldKey)),
        color: TOTAL_ROW_COLOR
      }), totalPerScenario.map(function (value, index, arr) {
        var _a, _b, _c;
        var baseValue = getChunkBaselineValue(totalPerScenario, index, chunkSize);
        var isMultiskillColumn = ((_a = data[index]) === null || _a === void 0 ? void 0 : _a.dataType) === QueueType.Multiskill;
        return _jsx(TableCell, {
          colSpan: 2,
          value: value,
          decimal: decimal,
          currency: currencySymbol,
          bold: true,
          color: isMultiskillColumn ? MULTISKILL_COLOR : (_c = PLAN_COLORS[(_b = data[index]) === null || _b === void 0 ? void 0 : _b.colorIndex]) === null || _c === void 0 ? void 0 : _c[1],
          highlightBorder: index + 1 !== arr.length && !((index + 1) % chunkSize),
          expandchildren: displayMode === DisplayMode.All ? undefined : generateVariances(baseValue !== null && baseValue !== void 0 ? baseValue : null, value, isPercentages, varianceOptions)
        }, "total-staff-".concat(valueKey, "-").concat(index));
      })]
    })]
  });
};
var SupportStaffSection = function (props) {
  return _jsx(SupportStaffGenericSection, __assign({}, props, {
    valueKey: "value",
    decimal: 1,
    totalFieldKey: "totalSupportStaff",
    totalAsParent: true
  }));
};
var SupportStaffSalariesSection = function (props) {
  var currency = useSelectCurrency();
  return _jsx(SupportStaffGenericSection, __assign({}, props, {
    valueKey: "supportStaffSalaries",
    decimal: 0,
    currencySymbol: currency,
    totalFieldKey: "supportStaffSalaries",
    totalAsParent: true
  }));
};
export var SupportStaffTable = function () {
  var t = useTranslation('report', {
    keyPrefix: 'annual'
  }).t;
  var _a = useSupportStaffExpansion('totalSupportStaff'),
    expansionItems = _a.expansionItems,
    getNestedExpansionItems = _a.getNestedExpansionItems;
  return _jsx(AnnualSectionTable, {
    title: t('supportStaff'),
    unit: "# ".concat(t('ofStaff')),
    expansionItems: expansionItems,
    tableBodyComponent: SupportStaffSection,
    getNestedExpansionItems: getNestedExpansionItems,
    defaultExpandedItems: ['totalSupportStaff']
  });
};
export var SupportStaffSalariesTable = function () {
  var t = useTranslation('report', {
    keyPrefix: 'annual'
  }).t;
  var _a = useSupportStaffExpansion('supportStaffSalaries'),
    expansionItems = _a.expansionItems,
    getNestedExpansionItems = _a.getNestedExpansionItems;
  var currency = useSelectCurrency();
  return _jsx(AnnualSectionTable, {
    title: t('reportRows.supportStaffSalaries', {
      currency: currency
    }),
    unit: t('chart.supportStaffSalaries'),
    expansionItems: expansionItems,
    getNestedExpansionItems: getNestedExpansionItems,
    tableBodyComponent: SupportStaffSalariesSection,
    defaultExpandedItems: ['supportStaffSalaries']
  });
};